<template>
  <DModal v-if="activeAbsenceReason" :show-close="false" :isBgOverlay="true" @close="close">
    <div slot="insertComponent" class="text-dark">
      <div class="d-modal__header">
        <h2><span v-if="isActiveAbsenceReasonIsReadyToDelete">{{
            $t('vue2.subs_ongoing_absence.delete_absence')
          }}</span><span
          v-else>{{ $t('vue2.subs_ongoing_absence.change_start_and_end_date') }}</span></h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="close"><span
          class="icon-cross2"></span></div>
      </div>
      <div class="d-modal__body d-modal__body--edit-absence">
        <div class="d-modal__body--edit-absence__header">
          <img :src="showUser.default_thumb_url"/>
          <div class="absent-user__name">
            <h3>{{ showUser.name }}</h3>
            <div class="reason">
              <p>{{ getReasonTitle(activeAbsenceReason) }}
              <p>
                <span v-if="isOngoing && isAbsenceOngoing" class="badge badge--orange badge--small">{{
                    $t('vue2.subs_ongoing_absence.ongoing_absence')
                  }}</span>
                <span v-if="!isOngoing && getIsAbsenceMultipleDays" class="badge badge--small"> {{
                    $t('vue2.substitution_user.multiple_days')
                  }}</span>
              </p>
            </div>
            <p>
              <span v-if="getSerialListItem">
              {{ getSerialListItem.from_date | date }}
              - {{ getSerialListItem.to_date | date }}, {{
                  getSerialListItem.from_time | showTime
                }} - {{ getSerialListItem.to_time | showTime }}
            </span>
              <span v-else>
              {{ activeAbsenceReason.start | showDate }} -
              <span>{{ activeAbsenceReason.end | showTime }}</span>
            </span>
            </p>
          </div>
        </div>
        <div class="d-modal__body--edit-absence__body">
          <div class="d-modal__page-1" v-if="!isActiveAbsenceReasonIsReadyToDelete">
            <div class="d-modal__body--edit-absence__body__input-group" v-if="reason">
              <label class="d-label marg-1-bottom">{{ $t('vue2.substitution.reason') }}</label>
              <div class="absence-reason">
                <div>
                  <DSelect :items="subsAbsenceReasonsSorted"
                           :multiple="false"
                           :items-selected="reason"
                           @update="setReason"
                           :placeholder="$t('vue2.substitution.choose_absence_reason')">
                    <div slot="noMatch">
                      <h4>
                        {{ $t('vue2.dselect.no_matching_results') }}
                      </h4>
                    </div>
                  </DSelect>
                </div>
              </div>
              <span class="error-message" v-if="showErrors">{{ $t('vue2.substitution.reason_must_be_filled') }}</span>
            </div>
            <div class="d-modal__body--edit-absence__body__input-group">
              <label class="d-label marg-1-bottom">{{ $t('vue2.subs_ongoing_absence.date_label') }}</label>
              <div class="flex">
                <DatepickerRange
                  :allowSameDate="true"
                  :startdate="originalStart"
                  :enddate="originalEnd"
                  @startchanged="setStartDate"
                  @endchanged="setEndDate">
                </DatepickerRange>
                <div class="absence-no-end-date"
                     :class="[{'absence-no-end-date--active': isAbsenceOngoing}, {'absence-no-end-date--disabled': !isOngoingAbsenceAllowed}]">
                  <div class="absence-no-end-date--not-allowed" v-if="!isOngoingAbsenceAllowed"></div>
                  <div class="absence-no-end-date__inner" @click.stop.prevent="isAbsenceOngoing = !isAbsenceOngoing">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <label>{{ $t('vue2.subs_ongoing_absence.ongoing_absence') }}</label>
                  </div>
                  <DQuestionMark>
                    <p slot="answer">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
              </div>
            </div>
            <div class="d-modal__body--edit-absence__body__input-group">
              <label class="d-label marg-1-bottom">{{ $t('vue2.substitution.time') }}</label>
              <TimeChanger
                :showIcon="true"
                :start="startTime"
                :end="endTime"
                @updateStart="setStartTime"
                @updateEnd="setEndTime">
              </TimeChanger>
            </div>
          </div>
          <p v-if="deleteAbsenceFollowingDates.length > 0">
            <label class="d-label">{{ $t('vue2.subs_ongoing_absence.absence_will_be_deleted') }}</label>
            <span
              v-for="(date, idx) in deleteAbsenceFollowingDates"><span
              v-if="idx > 0">, </span> {{ date | date }}</span></p>
          <div class="d-modal__page-2" v-if="originalDatesNotInNewRange.length > 0">
            <SubsDeleteAbsence
              :calendarId="this.activeAbsenceReason.calendar_id"
              :firstRange="originalDatesNotInNewRangeMinimum"
              :lastRange="originalDatesNotInNewRangeMaximum"
              :dates="originalDatesNotInNewRange"
              :startTime="startTime"
              :endTime="endTime"
              @lessonsWithSubs="setLessonsWithSubs"
              @gotLessons="isLessonsLoaded = true"
              @changed="setDeleteSubs">
            </SubsDeleteAbsence>
          </div>
        </div>
        <div class="d-modal__buttons d-modal__body--edit-absence__actions">
          <div>
            <button class="bttn bttn-danger marg-5-right" v-if="!isActiveAbsenceReasonIsReadyToDelete"
                    @click="$store.commit('setIsActiveAbsenceReasonIsReadyToDelete', true)"><span
              class="icon-bin"></span>
            </button>
          </div>
          <div class="d-modal__body--edit-absence__actions__right">
            <button class="marg-5-right" @click="close">{{ $t('vue2.common.cancel') }}</button>
            <!-- Fravær skal slettes -->
            <template v-if="isActiveAbsenceReasonIsReadyToDelete">
              <button @click="removeSubsAndDeleteAbsence" class="submit-button submit-button--danger">
                <!-- Fjern vikarer -->
                <span v-if="eventsToRemovedSubs.length > 0">{{
                    $t('vue2.subs_ongoing_absence.remove_subs_and_delete_absence')
                  }}</span>
                <!-- Der er vikarer men ikke fjern dem -->
                <span v-if="lessonsWithSubs.length > 0 && eventsToRemovedSubs.length < 1">{{
                    $t('vue2.subs_ongoing_absence.keep_subs_and_delete_absence')
                  }}</span>
                <!-- Ingen vikarer -->
                <span v-if="lessonsWithSubs.length < 1 && eventsToRemovedSubs.length < 1">{{
                    $t('vue2.subs_ongoing_absence.delete_absence')
                  }}</span>
              </button>
            </template>
            <!-- Fravær skal opdateres -->
            <template v-else>
              <!-- Måske skal der fjernes vikarer -->
              <button v-if="originalDatesNotInNewRange.length > 0"
                      class="submit-button"
                      @click="removeSubsAndUpdateAbsence">
                {{ $t('vue2.common.update') }}
              </button>
              <!-- Der skal ikke fjernes vikarer -->
              <button
                v-if="originalDatesNotInNewRange.length < 1 && this.eventsToRemovedSubs.length < 1"
                @click="updateDates"
                class="submit-button">
                {{ $t('vue2.common.update') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/da'
import {mapGetters} from 'vuex'
import moment from 'moment'
import SubsDeleteAbsence from './SubsDeleteAbsenceSubs'
import DatepickerRange from '../datepicker/DatePickerRange'
import SubsUserAbsenceReason from './SubsUserAbsenceReason'
import AppToggle from '../common/AppToggle'
import DQuestionMark from '../common/DQuestionMark'
import TimeChanger from '../TimeChanger'
import DSelect from '../DSelect/DSelect'

export default {
  components: {
    DModal,
    DatePicker,
    SubsDeleteAbsence,
    DatepickerRange,
    SubsUserAbsenceReason,
    AppToggle,
    DQuestionMark,
    TimeChanger,
    DSelect
  },
  data () {
    return {
      originalStartAndEnd: [],
      eventsToRemovedSubs: [],
      isLessonsLoaded: false,
      datePickerStart: null,
      datePickerEnd: null,
      startAndEnd: [],
      isAbsenceOngoing: false,
      lessonsWithSubs: [],
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      reason: [],
      showErrors: false
    }
  },
  filters: {
    showDate (date) {
      return moment(date).format('DD. MMM, LT')
    },
    showTime (date) {
      return moment(date).format('LT')
    },
    date (date) {
      return moment(date).format('ll')
    }
  },
  computed: {
    ...mapGetters([
      'allUsersInCalIdHash',
      'longAbsentListInHash',
      'subsAbsenceReasonsSorted'
    ]),
    activeAbsenceReason () {
      return this.$store.state.subs.activeAbsenceReason
    },
    isActiveAbsenceReasonIsReadyToDelete () {
      return this.$store.state.subs.isActiveAbsenceReasonIsReadyToDelete
    },
    showUser () {
      return this.allUsersInCalIdHash[this.activeAbsenceReason.calendar_id]
    },
    absenceReasons () {
      return this.$store.state.subs.absenceReasons
    },
    originalStart () {
      return moment(this.originalStartAndEnd[0]).format('YYYY-MM-DD')
    },
    originalEnd () {
      return moment(this.originalStartAndEnd[1]).format('YYYY-MM-DD')
    },
    originalDatesArr () {
      return this.enumerateDaysBetweenDates(this.originalStart, this.originalEnd)
    },
    datePickerDatesArr () {
      let start = moment(this.datePickerStart).format('YYYY-MM-DD')
      let end = moment(this.datePickerEnd).format('YYYY-MM-DD')
      return this.enumerateDaysBetweenDates(start, end)
    },
    originalDatesNotInNewRange () {
      return this.originalDatesArr.filter(e => this.datePickerDatesArr.indexOf(e) === -1)
    },
    originalDatesNotInNewRangeMinimum () {
      let moments = this.originalDatesNotInNewRange.map(d => moment(d))
      let date = moment.min(moments)
      let datepickerStart = moment(this.datePickerStart).format('YYYY-MM-DD')
      if (this.datePickerStart) {
        return moment(date).isBefore(this.datePickerStart) ? [date, moment(datepickerStart).subtract(1, 'day')] : false
      } else {
        return false
      }
    },
    originalDatesNotInNewRangeMaximum () {
      let moments = this.originalDatesNotInNewRange.map(d => moment(d))
      let date = moment.max(moments)
      let datepickerEnd = moment(this.datePickerEnd).format('YYYY-MM-DD')
      return moment(date).isAfter(this.datePickerEnd) ? [moment(datepickerEnd).add(1, 'day'), date] : false
    },
    deleteAbsenceFollowingDates () {
      return this.originalDatesNotInNewRange
    },
    getSerialListItem () {
      return this.activeAbsenceReason.event_serial_id ? this.longAbsentListInHash[this.activeAbsenceReason.event_serial_id] : false
    },
    isOngoing () {
      return this.getSerialListItem && this.getSerialListItem.ongoing
    },
    isOngoingAbsenceAllowed () {
      let endDate = moment(this.startAndEnd[1]).format('YYYY-MM-DD')
      let now = moment().format('YYYY-MM-DD')
      return moment(endDate).isSameOrAfter(now)
    }
  },
  watch: {
    isActiveAbsenceReasonIsReadyToDelete () {
      if (this.isActiveAbsenceReasonIsReadyToDelete) {
        this.datePickerStart = null
        this.datePickerEnd = null
      }
    }
  },
  methods: {
    getIsAbsenceMultipleDays () {
      let serialItem = this.getSerialListItem
      return serialItem && moment(serialItem.from_date).format('YYYY-MM-DD') !== moment(serialItem.to_date).format('YYYY-MM-DD')
    },
    setDeleteSubs (val) {
      this.eventsToRemovedSubs = val
    },
    enumerateDaysBetweenDates (startDate, endDate) {
      let date = []
      while (moment(startDate) <= moment(endDate)) {
        date.push(startDate)
        startDate = moment(startDate).add(1, 'days').format('YYYY-MM-DD')
      }
      return date
    },
    getReasonTitle (reason) {
      let idx = this.absenceReasons.findIndex(absenceSubject => absenceSubject.id === reason.subject_id)
      return idx > -1 ? this.absenceReasons[idx].name : ''
    },
    removeSubsAndUpdateAbsence () {
      const vuexObj = {
        events: this.eventsToRemovedSubs,
        userId: this.showUser.id,
        user_calendar_id: this.activeAbsenceReason.calendar_id
      }
      this.$store.dispatch('removeSubsFromEvents', vuexObj)
      this.updateDates()
    },
    updateDates () {
      if (!this.reason[0]) {
        this.showErrors = true
        return
      }
      let obj = JSON.parse(JSON.stringify(this.activeAbsenceReason)) // clone obj without reference
      obj['from_date'] = this.datePickerStart
      obj['to_date'] = this.datePickerEnd
      obj['ongoing'] = this.isAbsenceOngoing
      obj['to_time'] = this.endTime
      obj['from_time'] = this.startTime
      obj['subject_id'] = this.reason[0].id
      this.$store.dispatch('updateEventsSerial', obj)
      this.close()
    },
    close () {
      this.$store.commit('setActiveAbsenceReason', null)
      this.$store.commit('setIsActiveAbsenceReasonIsReadyToDelete', false)
    },
    removeSubsAndDeleteAbsence () {
      let obj = {
        events: this.eventsToRemovedSubs,
        userId: this.showUser.id,
        user_calendar_id: this.activeAbsenceReason.calendar_id
      }
      this.$store.dispatch('removeSubsFromEvents', obj)
      let reasonObj = {
        event_serial_id: this.activeAbsenceReason.event_serial_id,
        user_calendar_id: this.activeAbsenceReason.calendar_id,
        id: this.activeAbsenceReason.id
      }
      this.$store.dispatch('deleteAbsence', reasonObj)
      this.closeSchedulerPopover()
      this.close()
    },
    setStartDate (payload) {
      this.datePickerStart = moment(payload).format('YYYY-MM-DD')
      this.setStartAndEnd()
    },
    setEndDate (payload) {
      this.datePickerEnd = moment(payload).format('YYYY-MM-DD')
      this.setStartAndEnd()
      // er den nye slutdato før den moment() - så kan det ikke være ongoing
      let endDate = moment(payload).format('YYYY-MM-DD')
      let now = moment().format('YYYY-MM-DD')
      if (!moment(endDate).isSameOrAfter(now)) {
        this.isAbsenceOngoing = false
      }
    },
    setStartTime (payload) {
      this.startTime = payload
    },
    setEndTime (payload) {
      this.endTime = payload
    },
    setStartAndEnd () {
      this.startAndEnd = [moment(this.datePickerStart).format('YYYY-MM-DD'), moment(this.datePickerEnd).format('YYYY-MM-DD')]
    },
    setLessonsWithSubs (arr) {
      this.lessonsWithSubs = arr
    },
    setReason (reason) {
      this.reason = reason
      this.showErrors = false
    },
    closeSchedulerPopover () {
      this.$store.commit('setActiveUser', null)
      this.$store.commit('setActiveEvent', null)
      this.$store.dispatch('setActiveSchedulerEvent', {
        'left': -10,
        'right': -10,
        'color': '',
        'oldSubScoreMeta': [],
        'obj': null
      })
    }
  },
  beforeMount () {
    let startTime = this.getSerialListItem ? this.getSerialListItem.from_time : this.activeAbsenceReason.start
    this.startTime = moment(startTime).locale('LT')
    let endTime = this.getSerialListItem ? this.getSerialListItem.to_time : this.activeAbsenceReason.end
    this.endTime = moment(endTime).locale('LT')
    let idx = this.absenceReasons.findIndex(absenceSubject => absenceSubject.id === this.activeAbsenceReason.subject_id)
    this.reason = [this.absenceReasons[idx]]
    if (this.activeAbsenceReason.event_serial_id) {
      let reason = this.longAbsentListInHash[this.activeAbsenceReason.event_serial_id]
      this.originalStartAndEnd = [reason.from_date, reason.to_date]
      this.isAbsenceOngoing = reason.ongoing
      return
    }
    if (this.activeAbsenceReason.from_date) {
      this.originalStartAndEnd = [new Date(this.activeAbsenceReason.from_date), new Date(this.activeAbsenceReason.to_date)]
    } else {
      this.originalStartAndEnd = [this.activeAbsenceReason.start, this.activeAbsenceReason.end]
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal__body {
  width: 700px;
  padding: 0;
}

.d-modal__header {
  margin-bottom: 0;
}

.absent-user__name {
  flex: 1;
}

.absent-user__name p {
  font-weight: bold;
  font-size: 1rem;
}

.absent-user__name h3 > span {
  font-size: 1rem;
}

.d-modal__body--edit-absence .create__form-group {
  margin: 2rem 0;
}

.delete-absence-button {
  margin-left: 2rem;
}

.d-modal__body--edit-absence {
  max-height: 80vh;
  overflow-y: scroll;
  padding: 0;
}

.d-modal__body--edit-absence__header {
  display: flex;
  padding: 2rem;
  border-bottom: 1px solid var(--border-color);
}

.d-modal__body--edit-absence__header img {
  width: 45px;
  height: 45px;
  border-radius: 4px;
  margin-right: 1rem;
}

.reason {
  display: flex;
  flex: 1;
  justify-content: space-between;
}

.d-modal__body--edit-absence__body {
  padding: 2rem 2rem 1rem 2rem;
}

.d-modal__body--edit-absence__actions {
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.absence-no-end-date {
  display: flex;
  align-items: center;
  margin-left: 1rem;
  user-select: none;
  position: relative;
}

.absence-no-end-date.absence-no-end-date--disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.absence-no-end-date--not-allowed {
  cursor: not-allowed;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  background: rgb(255 255 255 / 50%);
  z-index: 11212122;
  border-radius: 50px;
  pointer-events: none;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-weight: bold;
}

.absence-no-end-date.absence-no-end-date--active label {
  color: var(--blue);
}

.absence-no-end-date label {
  margin-bottom: 0;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
}

.absence-no-end-date__inner {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.d-modal__body--edit-absence__body__input-group {
  margin-bottom: 1rem;
}

.error-message {
  color: #ff3860;
  font-size: 11px;
  font-weight: 400;
  padding: 1px 2px 2px;
}
</style>



// WEBPACK FOOTER //
// SubsEditAbsence.vue?56deaf8e